import { Suspense, type ReactNode } from "react";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle,
	DialogTrigger,
	useDialogCloseId,
} from "src/components/ui/dialog";
import { cn } from "src/lib/utils";
import Loader from "./Loader";

const modalVariants = cva(
	// Full-screen on mobile, centred card from `sm` up (responsive convention, D3).
	"tw-z-50 tw-flex tw-flex-col !tw-p-0 tw-max-w-none tw-w-screen tw-h-dvh tw-max-h-dvh tw-rounded-none tw-border-0 sm:tw-h-auto sm:tw-max-h-[85vh] sm:tw-w-full sm:tw-rounded-lg sm:tw-border",
	{
		variants: {
			size: {
				sm: "sm:!tw-max-w-xl",
				normal: "sm:!tw-max-w-3xl",
				lg: "sm:!tw-max-w-5xl",
				xl: "sm:!tw-max-w-7xl",
			},
		},
		defaultVariants: {
			size: "sm",
		},
	}
);

export type ModalSize = "sm" | "normal" | "lg" | "xl";

interface CustomModalBaseProps {
	title?: ReactNode;
	description?: ReactNode;
	children?: ReactNode;
	footer?: ReactNode;
	className?: string;
	size?: ModalSize;
}

/** Controlled: caller owns `open`. */
interface ControlledModalProps extends CustomModalBaseProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	trigger?: never;
}

/** Uncontrolled: the dialog opens from its own trigger element. */
interface TriggeredModalProps extends CustomModalBaseProps {
	trigger: ReactNode;
	open?: never;
	onOpenChange?: never;
}

export type CustomModalProps = ControlledModalProps | TriggeredModalProps;

const ModalBody = ({ title, description, children, footer }: CustomModalBaseProps) => {
	const closeId = useDialogCloseId();

	return (
		<>
			<div className="tw-sticky tw-top-0 tw-z-10 tw-h-fit tw-bg-white">
				<div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-border tw-px-4 tw-py-4 md:tw-px-6">
					<div className="tw-min-w-0 tw-flex-1">
						<DialogTitle className="tw-truncate tw-text-left tw-text-[17px] tw-font-semibold tw-text-text">{title}</DialogTitle>
						{description ? (
							<DialogDescription className="tw-mt-0.5 tw-text-left tw-text-[13px] tw-text-text-soft">
								{description}
							</DialogDescription>
						) : (
							// Radix warns when a dialog has no description; keep one for a11y.
							<DialogDescription className="tw-sr-only">{typeof title === "string" ? title : "Dialog"}</DialogDescription>
						)}
					</div>

					<DialogClose
						type="button"
						id={closeId}
						aria-label="Close"
						className="!tw-m-0 tw-flex tw-aspect-square tw-w-fit tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text"
					>
						<X className="tw-size-4" />
					</DialogClose>
				</div>
			</div>

			<div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-6 tw-pt-4 md:tw-px-6">
				<Suspense fallback={<Loader />}>{children}</Suspense>
			</div>

			{footer && (
				<DialogFooter className="tw-border-t tw-border-border tw-px-4 tw-py-3 md:tw-px-6 sm:tw-justify-start">{footer}</DialogFooter>
			)}
		</>
	);
};

export const CustomModal = ({ title, description, children, footer, className, size = "sm", ...rest }: CustomModalProps) => {
	const body = (
		<ModalBody title={title} description={description} footer={footer}>
			{children}
		</ModalBody>
	);

	if (rest.open !== undefined) {
		const { open, onOpenChange } = rest as ControlledModalProps;

		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className={cn(modalVariants({ size }), className)}>{body}</DialogContent>
			</Dialog>
		);
	}

	const { trigger } = rest as TriggeredModalProps;

	return (
		<Dialog>
			<DialogTrigger asChild>{trigger}</DialogTrigger>
			<DialogContent className={cn(modalVariants({ size }), className)}>{body}</DialogContent>
		</Dialog>
	);
};

export default CustomModal;
